import * as React from 'react';
import { useEffect, useState } from 'react';
import { LoadingButton } from '@mui/lab';
import { Button, FormControlLabel, Paper, Switch, TextField, Tooltip } from '@mui/material';
import { sendServerRequest } from '../../../server/request';
import { openGlobalSnack } from '../../utils/snack';
import { CircularLoading } from '../../utils/loading';

const ENDPOINT = '/server/cltCommand/offlineRules';

type Target = 'v8' | 'celcoin' | 'uy3';

interface OfflineRule {
  minAge: number;
  maxAge: number;
  minWorkTimeMonths: number;
  minMargin: number;
  marginFactor: number;
  minCompanyMonths: number;
  blockMicroCompany: boolean;
  requireActiveCompany: boolean;
  blockedNaturezas: number[];
  blockedCnaePrefixes: string[];
}

type Rules = Record<Target, OfflineRule>;

const TARGETS: Target[] = ['v8', 'celcoin', 'uy3'];

const LABELS: Record<Target, string> = {
  v8: 'V8',
  celcoin: 'Suma · Celcoin',
  uy3: 'Suma · UY3',
};

/** Campos numéricos, na ordem em que aparecem no card. */
const NUMBER_FIELDS: {
  key: keyof OfflineRule;
  label: string;
  help: string;
  step?: string;
}[] = [
  { key: 'minAge', label: 'Idade mínima', help: 'Anos. Abaixo disso o provedor não é consultado.' },
  { key: 'maxAge', label: 'Idade máxima', help: 'Anos. Acima disso o provedor não é consultado.' },
  {
    key: 'minWorkTimeMonths',
    label: 'Carteira mín. (meses)',
    help: 'Tempo mínimo no vínculo atual, em meses (ex.: 12 = 1 ano).',
  },
  {
    key: 'minMargin',
    label: 'Margem mínima (R$)',
    help: 'Elegível quando margem × fator for MAIOR que este valor.',
  },
  {
    key: 'marginFactor',
    label: 'Fator da margem',
    help: 'Quanto da margem o provedor aproveita (Suma usa 0,75; V8 usa 1).',
    step: '0.05',
  },
  {
    key: 'minCompanyMonths',
    label: 'CNPJ aberto há (meses)',
    help: 'Tempo mínimo de abertura da empresa. 0 = não verifica.',
  },
];

/** Extrai a mensagem de erro do JSONResponse ({data, hasError, error}). */
function parseError(err: any, fallback: string): string {
  try {
    const txt = err?.target?.responseText;
    if (txt) {
      const j = JSON.parse(txt);
      if (j?.error && typeof j.error === 'string') return j.error;
    }
  } catch (e) {}
  return fallback;
}

/** "4711, 4712" → ["4711","4712"] (só dígitos). */
function parseList(value: string): string[] {
  return value
    .split(',')
    .map((v) => v.replace(/\D/g, '').trim())
    .filter(Boolean);
}

export function OfflineRulesPanel() {
  const [rules, setRules] = useState<Rules | null>(null);
  const [defaults, setDefaults] = useState<Rules | null>(null);
  const [serverSnapshot, setServerSnapshot] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    load();
  }, []);

  function applyRules(data: any, withDefaults?: boolean) {
    const r: Rules = data?.rules;
    if (!r) return;
    setRules(r);
    setServerSnapshot(JSON.stringify(r));
    if (withDefaults && data?.defaults) setDefaults(data.defaults);
  }

  function load() {
    setLoading(true);
    sendServerRequest('GET', `${ENDPOINT}/get`)
      .then((e) => {
        applyRules(e.data?.data, true);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        openGlobalSnack('Não foi possível carregar as regras da pré-análise.', 'error');
      });
  }

  function update(target: Target, patch: Partial<OfflineRule>) {
    setRules((prev) => (prev ? { ...prev, [target]: { ...prev[target], ...patch } } : prev));
  }

  function setNumber(target: Target, key: keyof OfflineRule, value: string) {
    const n = Number(value.replace(',', '.'));
    update(target, { [key]: Number.isFinite(n) && n >= 0 ? n : 0 } as Partial<OfflineRule>);
  }

  function save() {
    if (!rules) return;
    setSaving(true);
    sendServerRequest('POST', `${ENDPOINT}/set`, { rules })
      .then((e) => {
        applyRules(e.data?.data);
        setSaving(false);
        openGlobalSnack('Regras da pré-análise atualizadas.', 'success');
      })
      .catch((err) => {
        setSaving(false);
        openGlobalSnack(parseError(err, 'Não foi possível salvar as regras.'), 'error');
      });
  }

  function resetTarget(target: Target) {
    sendServerRequest('POST', `${ENDPOINT}/reset`, { target })
      .then((e) => {
        applyRules(e.data?.data);
        openGlobalSnack(`Regras de ${LABELS[target]} restauradas.`, 'success');
      })
      .catch((err) => {
        openGlobalSnack(parseError(err, 'Não foi possível restaurar as regras.'), 'error');
      });
  }

  const dirty = !!rules && JSON.stringify(rules) !== serverSnapshot;

  if (loading) return <CircularLoading />;
  if (!rules) return null;

  return (
    <div style={{ maxWidth: 760, margin: '0 auto', textAlign: 'center' }}>
      <h3 style={{ marginBottom: 4 }}>Regras da pré-análise (consulta offline)</h3>
      <p style={{ fontSize: 12, color: '#000000a0', marginTop: 0, marginBottom: 16 }}>
        Filtro que roda ANTES da consulta online, com os dados que já temos (DataPrev + CNPJ):
        quem não passa nas regras do provedor não é consultado nele, e a consulta segue
        para o próximo. Regra sem dado disponível é ignorada — ninguém é reprovado por
        falta de informação.
      </p>

      {TARGETS.map((target) => {
        const rule = rules[target];
        if (!rule) return null;
        const def = defaults?.[target];
        return (
          <Paper
            key={target}
            variant="outlined"
            style={{ padding: '12px 16px', marginBottom: 16, textAlign: 'left' }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 12,
              }}
            >
              <strong>{LABELS[target]}</strong>
              <Button size="small" onClick={() => resetTarget(target)}>
                Restaurar padrão
              </Button>
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
              {NUMBER_FIELDS.map((field) => (
                <Tooltip key={String(field.key)} title={field.help}>
                  <TextField
                    label={field.label}
                    type="number"
                    size="small"
                    value={rule[field.key] as number}
                    onChange={(e) => setNumber(target, field.key, e.target.value)}
                    inputProps={{ min: 0, step: field.step ?? '1' }}
                    helperText={def ? `padrão: ${def[field.key]}` : ' '}
                    style={{ width: 165 }}
                  />
                </Tooltip>
              ))}
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginTop: 4 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={rule.blockMicroCompany}
                    onChange={(e) => update(target, { blockMicroCompany: e.target.checked })}
                  />
                }
                label="Reprovar ME/MEI"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={rule.requireActiveCompany}
                    onChange={(e) => update(target, { requireActiveCompany: e.target.checked })}
                  />
                }
                label="Exigir CNPJ ativo"
              />
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 12 }}>
              <TextField
                label="CNAEs bloqueados"
                size="small"
                value={rule.blockedCnaePrefixes.join(', ')}
                onChange={(e) =>
                  update(target, { blockedCnaePrefixes: parseList(e.target.value) })
                }
                helperText="Prefixos separados por vírgula (ex.: 4711, 5611, 14)."
                style={{ flex: 1, minWidth: 260 }}
              />
              <TextField
                label="Naturezas jurídicas bloqueadas"
                size="small"
                value={rule.blockedNaturezas.join(', ')}
                onChange={(e) =>
                  update(target, {
                    blockedNaturezas: parseList(e.target.value).map((n) => Number(n)),
                  })
                }
                helperText="Códigos separados por vírgula (ex.: 3999)."
                style={{ flex: 1, minWidth: 260 }}
              />
            </div>
          </Paper>
        );
      })}

      <div style={{ marginTop: 8 }}>
        <LoadingButton loading={saving} disabled={!dirty} variant="contained" onClick={save}>
          Salvar regras
        </LoadingButton>
      </div>
    </div>
  );
}
